import React, { useEffect } from 'react';
import { X, Download, MapPin, Briefcase, Euro, EyeOff, Eye } from 'lucide-react';
import Logo from '../shared/Logo';
import type { TalentProfile } from '../../types';

interface CandidatePresentationProps {
    profile: TalentProfile & { photo_url?: string | null };
    onClose: () => void;
    onExportPDF: () => void;
    isExporting?: boolean;
    isAnonymized?: boolean;
    onToggleAnonymize?: () => void;
}

// Generate a deterministic color from a name for the avatar background
const getAvatarColor = (name: string): string => {
    const colors = [
        'bg-slate-700', 'bg-blue-700', 'bg-indigo-700',
        'bg-teal-700', 'bg-emerald-700', 'bg-violet-700',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const CandidatePresentation: React.FC<CandidatePresentationProps> = ({
    profile,
    onClose,
    onExportPDF,
    isExporting = false,
    isAnonymized = false,
    onToggleAnonymize,
}) => {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const nameParts = profile.name.trim().split(/\s+/);
    const initials = nameParts.map(p => p[0]?.toUpperCase()).slice(0, 2).join('');
    const displayName = isAnonymized ? initials : profile.name;

    const avatarColor = getAvatarColor(profile.name);

    // Top 3 missions pour la sélection de références
    const topMissions = profile.timeline.slice(0, 3);

    // Top compétences (max 10 pour la fiche)
    const topSkills = [
        ...profile.skills.technical.slice(0, 5).map(s => ({ ...s, type: 'tech' as const })),
        ...profile.skills.methods.slice(0, 3).map(s => ({ ...s, type: 'method' as const })),
        ...profile.skills.soft.slice(0, 2).map(s => ({ ...s, type: 'soft' as const })),
    ].filter(s => s.name);

    const availabilityLabel = profile.availability_status === 'disponible'
        ? 'Disponible'
        : profile.availability_status === 'listening'
        ? 'À l\'écoute'
        : null;

    const generatedDate = new Date().toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    return (
        /* ── Backdrop overlay ── */
        <div className="fixed inset-0 z-[80] flex flex-col bg-slate-950 animate-in fade-in duration-200">

            {/* ── Toolbar ── */}
            <div className="flex items-center justify-between px-6 py-3 bg-slate-900 shrink-0">
                <div className="flex items-center gap-3">
                    <Logo size={24} />
                    <span className="text-white font-semibold text-sm">Fiche de présentation</span>
                    <span className="text-slate-400 text-sm">— {displayName}</span>
                </div>
                <div className="flex items-center gap-3">
                    {onToggleAnonymize && (
                        <button
                            onClick={onToggleAnonymize}
                            title={isAnonymized ? 'Afficher le nom' : 'Anonymiser le nom'}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                                isAnonymized
                                    ? 'bg-amber-500/20 border-amber-500/40 text-amber-300 hover:bg-amber-500/30'
                                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white'
                            }`}
                        >
                            {isAnonymized ? <EyeOff size={14} /> : <Eye size={14} />}
                            {isAnonymized ? 'Anonymisé' : 'Anonymiser'}
                        </button>
                    )}
                    <button
                        onClick={onExportPDF}
                        disabled={isExporting}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-60"
                    >
                        <Download size={15} />
                        {isExporting ? 'Export en cours…' : 'Télécharger PDF'}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* ── Scrollable area ── */}
            <div className="flex-1 overflow-y-auto py-8 px-4 flex justify-center">

                {/* ── A4 Card ── */}
                <div
                    id="candidate-presentation-card"
                    className="bg-white w-[794px] min-h-[1123px] shadow-2xl flex flex-col font-sans"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >

                    {/* ═══ HEADER ═══ */}
                    <div className="bg-slate-900 px-8 py-6 flex items-center gap-6" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        {/* Avatar / Photo */}
                        <div className="shrink-0">
                            {profile.photo_url && !isAnonymized ? (
                                <img
                                    src={profile.photo_url}
                                    alt={displayName}
                                    className="w-20 h-20 rounded-full object-cover border-2 border-slate-600"
                                />
                            ) : (
                                <div className={`w-20 h-20 rounded-full ${avatarColor} flex items-center justify-center border-2 border-slate-600`}>
                                    <span className="text-white font-bold text-2xl">{initials}</span>
                                </div>
                            )}
                        </div>

                        {/* Identity */}
                        <div className="flex-1 min-w-0">
                            <h1 className="text-white font-bold text-2xl leading-tight">{displayName}</h1>
                            <p className="text-amber-400 font-semibold text-base mt-0.5">{profile.role}</p>
                            <div className="flex flex-wrap items-center gap-4 mt-2">
                                {profile.contactInfo?.location_city && (
                                    <span className="flex items-center gap-1.5 text-slate-300 text-[13px]">
                                        <MapPin size={12} className="text-slate-400" />
                                        {profile.contactInfo.location_city}
                                    </span>
                                )}
                                {profile.experience && (
                                    <span className="flex items-center gap-1.5 text-slate-300 text-[13px]">
                                        <Briefcase size={12} className="text-slate-400" />
                                        {profile.experience} d'expérience
                                    </span>
                                )}
                                {availabilityLabel && (
                                    <span className="flex items-center gap-1.5 text-emerald-400 text-[13px] font-medium">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                        {availabilityLabel}
                                    </span>
                                )}
                                {(profile.tjm_min || profile.tjm_max) && (
                                    <span className="flex items-center gap-1.5 text-blue-300 text-[13px] font-medium">
                                        <Euro size={12} />
                                        {profile.tjm_min && profile.tjm_max
                                            ? `${profile.tjm_min}–${profile.tjm_max} €/j`
                                            : profile.tjm_min ? `≥${profile.tjm_min} €/j` : `≤${profile.tjm_max} €/j`}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Role fit badges */}
                        {profile.role_fit_titles && profile.role_fit_titles.length > 0 && (
                            <div className="shrink-0 flex flex-col gap-1.5 items-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
                                {profile.role_fit_titles.slice(0, 3).map((title, i) => (
                                    <span key={i} className="px-2.5 py-1 bg-slate-700 text-slate-200 rounded text-[11px] font-semibold border border-slate-600 whitespace-nowrap">
                                        {title}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ═══ BODY ═══ */}
                    <div className="flex flex-1 divide-x divide-slate-100" style={{ display: 'flex', flex: '1 1 auto', alignItems: 'stretch' }}>

                        {/* ── LEFT COLUMN (38%) ── */}
                        <div className="w-[38%] shrink-0 p-6 space-y-5 bg-slate-50/50">

                            {/* Biographie */}
                            {(profile.profile_summary || profile.summary) && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Biographie</h2>
                                    <p className="text-[12.5px] text-slate-700 leading-relaxed">
                                        {profile.profile_summary || profile.summary}
                                    </p>
                                </section>
                            )}

                            {/* Facteur différenciant */}
                            {profile.top_strengths && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Atout Clé</h2>
                                    <p className="text-[12px] text-slate-700 leading-relaxed font-medium italic">
                                        "{profile.top_strengths}"
                                    </p>
                                </section>
                            )}

                            {/* Compétences */}
                            {topSkills.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Compétences Clés</h2>
                                    <div className="flex flex-wrap gap-1.5">
                                        {topSkills.map((skill, i) => (
                                            <span
                                                key={i}
                                                className={`px-2 py-0.5 rounded text-[10.5px] font-semibold border ${
                                                    skill.type === 'tech'
                                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                        : skill.type === 'method'
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                                }`}
                                            >
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Langues */}
                            {profile.languages && profile.languages.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Langues</h2>
                                    <div className="flex flex-wrap gap-1.5">
                                        {profile.languages.map((lang, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[11px] font-medium border border-slate-200">
                                                {lang}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Formation */}
                            {profile.education && profile.education.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Formation</h2>
                                    <ul className="space-y-2">
                                        {profile.education.slice(0, 3).map((edu, i) => (
                                            <li key={i}>
                                                <div className="text-[12px] font-bold text-slate-800">{edu.degree}</div>
                                                <div className="text-[11px] text-slate-500">{[edu.school, edu.year].filter(Boolean).join(' · ')}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {/* Certifications */}
                            {profile.certifications && profile.certifications.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2">Certifications</h2>
                                    <div className="flex flex-wrap gap-1.5">
                                        {profile.certifications.slice(0, 5).map((cert, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10.5px] font-medium border border-slate-200">
                                                {cert}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* ── RIGHT COLUMN (62%) ── */}
                        <div className="flex-1 p-6 space-y-5">

                            {/* Sélection de missions */}
                            {topMissions.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-3">Sélection de Références</h2>
                                    <div className="space-y-4">
                                        {topMissions.map((item, idx) => (
                                            <div key={idx} className="pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                                                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                                                    <h3 className="text-[13px] font-bold text-slate-900 leading-snug">
                                                        {item.company} — {item.role}
                                                    </h3>
                                                    <span className="text-[10.5px] text-slate-400 font-medium shrink-0">{item.year}</span>
                                                </div>

                                                {item.summary && (
                                                    <ul className="mt-1.5 space-y-0.5">
                                                        {(item.summary.includes('•')
                                                            ? item.summary.split(/\s*•\s*/).map(s => s.trim()).filter(Boolean)
                                                            : item.summary.split(/\.\s+/).map(s => s.trim()).filter(Boolean)
                                                        ).slice(0, 4).map((bullet, bIdx) => (
                                                            <li key={bIdx} className="flex items-start gap-2 text-[11.5px] text-slate-600 leading-snug">
                                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                                                                {bullet.endsWith('.') ? bullet : `${bullet}.`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                {item.tags && item.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                        {item.tags.slice(0, 5).map(t => (
                                                            <span key={t} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded text-[9.5px] font-semibold">
                                                                {t}
                                                            </span>
                                                        ))}
                                                        {item.methodTags?.slice(0, 3).map(t => (
                                                            <span key={t} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded text-[9.5px] font-semibold">
                                                                {t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Top Réalisations */}
                            {profile.topAchievements && profile.topAchievements.length > 0 && (
                                <section>
                                    <h2 className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-3">Top Réalisations</h2>
                                    <ul className="space-y-2">
                                        {profile.topAchievements.slice(0, 3).map((ach, i) => (
                                            <li key={i} className="flex items-start gap-2.5">
                                                <div className="mt-0.5 w-4 h-4 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0 text-[9px] font-bold">
                                                    {i + 1}
                                                </div>
                                                <span className="text-[12px] text-slate-700 leading-snug font-medium">{ach}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    </div>

                    {/* ═══ FOOTER ═══ */}
                    <div className="border-t border-slate-200 px-8 py-3 flex items-center justify-between bg-slate-50">
                        <div className="flex items-center gap-2">
                            <Logo size={18} />
                            <span className="text-slate-400 text-[11px] font-semibold">RevertAI</span>
                            <span className="text-slate-300 text-[11px]">·</span>
                            <span className="text-slate-400 text-[11px]">Généré le {generatedDate}</span>
                        </div>
                        <span className="text-slate-300 text-[10px]">Document confidentiel — usage interne</span>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CandidatePresentation;
